export function getContrastingColor(
    bgColor,
    light = "#ffffff",
    dark = "#111111",
    minContrast = 4.5
) {
    const bg = parseColorToRGB(bgColor);
    if (!bg) return dark;

    const lightRgb = parseColorToRGB(light);
    const darkRgb = parseColorToRGB(dark);

    const crLight = contrastRatio(bg, lightRgb);
    const crDark = contrastRatio(bg, darkRgb);

    if (crLight >= minContrast || crDark >= minContrast) {
        return crLight >= crDark ? light : dark;
    }
    return crLight >= crDark ? light : dark;
}

export function getReadableTextColor(
    bgColor,
    options = { light: "#ffffff", dark: "#111111", minContrast: 4.5 }
) {
    const { light, dark, minContrast } = options;
    return getContrastingColor(bgColor, light, dark, minContrast);
}

export function contrastRatio(rgbA, rgbB) {
    const l1 = relativeLuminance(rgbA);
    const l2 = relativeLuminance(rgbB);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}

export function relativeLuminance({ r, g, b }) {
    const toLinear = (v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const R = toLinear(r);
    const G = toLinear(g);
    const B = toLinear(b);
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

export function parseColorToRGB(input) {
    if (!input || typeof input !== "string") return null;
    const c = input.trim().toLowerCase();

    if (c.startsWith("#")) {
        const hex = c.slice(1);
        const h = hex.length === 3 ? hex.split("").map((ch) => ch + ch).join("") : hex;
        if (h.length !== 6 || !/^[0-9a-f]{6}$/.test(h)) return null;
        return {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
        };
    }

    const mRgb = c.match(/^rgba?\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})(?:\s*,\s*(0|0?\.\d+|1))?\s*\)$/);
    if (mRgb) {
        const r = clampInt(parseInt(mRgb[1], 10), 0, 255);
        const g = clampInt(parseInt(mRgb[2], 10), 0, 255);
        const b = clampInt(parseInt(mRgb[3], 10), 0, 255);
        return { r, g, b };
    }

    const mHsl = c.match(/^hsla?\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})%\s*,\s*([0-9]{1,3})%(?:\s*,\s*(0|0?\.\d+|1))?\s*\)$/);
    if (mHsl) {
        const h = clampInt(parseInt(mHsl[1], 10), 0, 360);
        const s = clampInt(parseInt(mHsl[2], 10), 0, 100) / 100;
        const l = clampInt(parseInt(mHsl[3], 10), 0, 100) / 100;
        return hslToRgb(h, s, l);
    }

    const named = NAMED_COLORS[c];
    if (named) {
        return parseColorToRGB(named);
    }

    return null;
}

export function rgbToHex({ r, g, b }) {
    const h = (n) => n.toString(16).padStart(2, "0");
    return `#${h(clampInt(r, 0, 255))}${h(clampInt(g, 0, 255))}${h(clampInt(b, 0, 255))}`;
}

export function shade(color, amount = -0.1) {
    const rgb = parseColorToRGB(color);
    if (!rgb) return color;
    const mix = (v) => clampInt(Math.round(v + (amount > 0 ? (255 - v) * amount : v * amount)), 0, 255);
    return rgbToHex({ r: mix(rgb.r), g: mix(rgb.g), b: mix(rgb.b) });
}

export function hslToRgb(h, s, l) {
    // h: 0-360, s/l: 0-1
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const hp = h / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));

    let r1 = 0, g1 = 0, b1 = 0;
    if (hp >= 0 && hp < 1) [r1, g1, b1] = [c, x, 0];
    else if (hp >= 1 && hp < 2) [r1, g1, b1] = [x, c, 0];
    else if (hp >= 2 && hp < 3) [r1, g1, b1] = [0, c, x];
    else if (hp >= 3 && hp < 4) [r1, g1, b1] = [0, x, c];
    else if (hp >= 4 && hp < 5) [r1, g1, b1] = [x, 0, c];
    else if (hp >= 5 && hp <= 6) [r1, g1, b1] = [c, 0, x];

    const m = l - c / 2;
    return {
        r: clampInt(Math.round((r1 + m) * 255), 0, 255),
        g: clampInt(Math.round((g1 + m) * 255), 0, 255),
        b: clampInt(Math.round((b1 + m) * 255), 0, 255),
    };
}

function clampInt(v, min, max) {
    return Math.max(min, Math.min(max, v));
}

const NAMED_COLORS = {
    black: "#000000",
    white: "#ffffff",
    red: "#ff0000",
    green: "#00ff00",
    blue: "#0000ff",
    gray: "#808080",
    grey: "#808080",
    silver: "#c0c0c0",
    maroon: "#800000",
    olive: "#808000",
    navy: "#000080",
    teal: "#008080",
    purple: "#800080",
};
